/**
 * Funnel events for marketing → registration → first cleanup.
 * Registration and first cleanup conversions are the canonical sign_up and
 * first_tool_use events. This file does not send those names again.
 * No photo content or PII.
 */
import { isDuplicateConversionEvent } from '@rehub/tracking';

const FIRST_UPLOAD_PREFIX = 'photoclear_first_upload_';
const FIRST_CLEANUP_PREFIX = 'photoclear_first_cleanup_';
const SIGNUP_PREFIX = 'photoclear_registration_';

function emit(event: string, params: Record<string, string | number | boolean> = {}): void {
  if (typeof window === 'undefined') return;
  if (isDuplicateConversionEvent(event)) return;
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return;
  try {
    gtag('event', event, params);
  } catch {
    /* analytics must never break the app */
  }
}

function once(key: string): boolean {
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

export function trackRegistrationComplete(userId: string, cta?: string | null): void {
  if (!once(`${SIGNUP_PREFIX}${userId}`)) return;
  emit('registration_complete', {
    app: 'photoclear',
    ...(cta ? { cta_location: cta } : {}),
  });
  emit('signup', { app: 'photoclear' });
}

export function trackFirstUpload(userId: string): void {
  if (!once(`${FIRST_UPLOAD_PREFIX}${userId}`)) return;
  emit('first_upload', { app: 'photoclear' });
}

export function trackFirstCleanup(userId: string): void {
  void recordFirstToolUse(userId);
  if (!once(`${FIRST_CLEANUP_PREFIX}${userId}`)) return;
  emit('first_successful_cleanup', { app: 'photoclear' });
  emit('first_generation', { app: 'photoclear', feature: 'declutter' });
}

async function recordFirstToolUse(userId: string): Promise<void> {
  try {
    const tracking = await import('@rehub/tracking');
    const { supabase } = await import('../services/supabase');
    const rpc = async (fn: string, args: Record<string, unknown>) => {
      const { data, error } = await supabase.rpc(fn, args);
      return { data, error: error ? { message: error.message } : null };
    };
    const result = await tracking.notifyToolSuccess(rpc, 'photoclear');
    const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
    if (!tracking.platformTagsEnabled({ hostname: window.location.hostname, env })) return;
    if (result.eventId !== tracking.firstToolEventId(userId)) return;
    const consent = tracking.parseConsent(tracking.readCookie(document.cookie, tracking.CONSENT_COOKIE));
    if (!consent || (!consent.analytics && !consent.advertising)) return;
    const row = await tracking.claimCanonicalEvent(rpc, result.eventId, consent, null);
    if (!row || row.claimed !== true || typeof row.event_id !== 'string') return;
    const config = tracking.tagConfigFromEnv(env);
    const target = tracking.rehubBrowserTarget(window, config);
    if (!target.gtag && !target.fbq) return;
    tracking.dispatchBrowserEvent({
      event: 'first_tool_use',
      eventId: row.event_id,
      params: { app: 'photoclear', app_name: 'photoclear', content_name: 'photoclear' },
      config,
      consent,
      storage: window.sessionStorage,
      target,
    });
  } catch (error) {
    console.warn('First tool use was not recorded:', error);
  }
}

export function trackLoginShown(mode: 'login' | 'signup'): void {
  emit('login_shown', { app: 'photoclear', mode });
}
